// src/pages/Home.jsx
import React, { useMemo, useState, useEffect, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AuthModal from "../components/AuthModal";
import "./css/Home.css";
import FollowedCreatorsPanel from "../components/FollowedCreatorsPanel";
import BookmarksPanel from "../components/BookmarksPanel";
import SearchBar from "../components/SearchBar";
import NewMembers from "../components/NewMembers";
import QuickStats from "../components/QuickStats";
import useTopTags from "../hooks/useTopTags";
import ExplorePage from "../pages/Explore";
import { useToast } from "../components/ToastProvider";
import { addTrip } from "../services/trips";
import GuideCard from "../components/GuideCard";
import { collection, query, where, limit, getDocs, getDoc, doc } from "firebase/firestore";
import { db } from "../firebase";

// Lazy placeholder: we'll load AddTripModal dynamically when needed.
// Note: we still use dynamic import manually so we can support both default or named exports.
function Skeleton({ height = 44 }) {
  return <div className="skel" style={{ height, borderRadius: 8 }} aria-hidden="true" />;
}
const EditorPicksSection = React.lazy(() =>
  import("../components/editor/EditorPicksSection")
);

function UserCard({ user }) {
  return (
    <div className="creator-card">
      <img src={user.avatar || "/default-avatar.png"} alt="" className="creator-avatar" />
      <div className="creator-meta">
        <div className="creator-name">{user.name}</div>
        <div className="creator-sub">{user.location || user.headline}</div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { addToast } = useToast(); // global toast hook

  // start with empty arrays — no prefilled/demo data
  const [users, setUsers] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  // No remote fetch for now — UI will display "no data" states.
  const [loading] = useState(false);

  // UI state (local)
  const [authOpen, setAuthOpen] = useState(false);

  // Add-trip modal handling:
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [AddModalComp, setAddModalComp] = useState(null);
  const { tags: trendingTags, loading: tagsLoading } = useTopTags(12);

  // near other useState declarations
  const [exploreInlineOpen, setExploreInlineOpen] = useState(false);
  const [exploreInitialTag, setExploreInitialTag] = useState(null);
  const [featuredGuides, setFeaturedGuides] = useState([]);
  const [guidesLoading, setGuidesLoading] = useState(true);

  useEffect(() => {
    import("../components/editor/EditorPicksSection");
  }, []);

  useEffect(() => {
    const fetchFeaturedGuides = async () => {
      try {
        const q = query(
          collection(db, "guides"),
          limit(6)
        );

        const snap = await getDocs(q);

        const guides = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setFeaturedGuides(guides);
      } catch (err) {
        console.error("Failed to fetch featured guides", err);
      } finally {
        setGuidesLoading(false);
      }
    };

    fetchFeaturedGuides();
  }, []);

  // Handler for opening add-trip that tries dynamic import first, otherwise dispatches fallback.
  const openAddTripModal = async () => {
    if (!currentUser) {
      addToast({ msg: "Please log in or sign up to add a trip.", type: "warning" }, 3500);
      return;
    }

    // Always notify app-level listener first so central TripModal (in App.js) will open.
    window.dispatchEvent(new CustomEvent("jift:openAddTrip"));

    // Try to dynamically import TripModal so Home can open an inline one if desired.
    // But don't *require* it — even if import fails, app-level modal will already open.
    try {
      const imported = await import("../components/TripModal");
      const Comp = imported.default || imported.TripModal || imported;
      if (Comp) {
        // If we successfully imported, cache the component and open an inline modal (optional UX).
        setAddModalComp(() => Comp);
        setAddModalOpen(true);
      } else {
        console.warn("[Home] dynamic import succeeded but component not found/exported as default.");
      }
    } catch (err) {
      // Import failing is non-fatal now — central modal is already triggered.
      console.warn("[Home] dynamic import('../components/TripModal') failed:", err);
    }
  };

  // Listen for global open event (so Navbar dispatch will open modal in Home)
  useEffect(() => {
    const onOpen = async (e) => {
      // If already loaded component, just open it
      if (AddModalComp) {
        setAddModalOpen(true);
        return;
      }

      // Try to load the real TripModal (your actual file)
      try {
        const imported = await import("../components/TripModal");
        const Comp = imported.default || imported.TripModal || imported;
        if (Comp) {
          setAddModalComp(() => Comp);
          setAddModalOpen(true);
          return;
        }
      } catch (err) {
        // import failed — continue to fallback below
        // console.warn("[Home] import TripModal failed", err);
      }

      // Final UI feedback if nothing opened
      addToast({ msg: "Add Trip modal unavailable here — ensure TripModal exists or open it from the navbar.", type: "warning" }, 5000);
    };

    window.addEventListener("jift:openAddTrip", onOpen);
    return () => window.removeEventListener("jift:openAddTrip", onOpen);
  }, [AddModalComp, addToast]);

  // If user clicks button inside Home page
  const handleAddTripClick = (e) => {
    if (!currentUser) {
      addToast({ msg: "Please log in or sign up to add a trip.", type: "warning" }, 3500);
      return;
    }
    // Try to open modal by loading TripModal locally (falls back to event only if import fails)
    openAddTripModal();
  };

  const handleCreateProfileClick = () => {
    setAuthOpen(true);
  };

  return (
    <div className="hp-container">
      <header className="hp-hero">
        <div className="hp-brand">
          <div className="hp-logo">Jift</div>
          <h1 className="hp-title">Travelogue — explore real trips</h1>
          <p className="hp-sub">
            Discover curated trips, follow creators, and share your own
            adventures. Beautiful maps, galleries and travel stories — ready to
            inspire your next journey.
          </p>
          <div style={{ marginTop: 12 }}>
            <button className="hp-btn primary" style={{ marginRight: 8 }} onClick={() => setExploreInlineOpen(true)}>
              Explore Trips
            </button>
            {!currentUser && (
              <button className="hp-btn ghost" onClick={handleCreateProfileClick}>
                Create profile
              </button>
            )}

            <>
              <button
                className="hp-btn link"
                style={{ marginLeft: 8 }}
                onClick={handleAddTripClick}
              >
                + Add trip
              </button>
            </>
          </div>
        </div>

        <div className="hp-search">
          <SearchBar users={users} trips={featured} />
        </div>

      </header>

      <section className="hp-grid">
        <div className="left-column">
          <div className="card">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <h2 style={{ margin: 0 }}>Featured</h2>
              {/* <Link to="/explore">See all</Link> */}
            </div>

            <div className="featured-grid" style={{ marginTop: 10 }}>
              {/* Guides first */}
              {guidesLoading ? (
                Array.from({ length: 2 }).map((_, i) => (
                  <Skeleton key={i} height={160} />
                ))
              ) : featuredGuides.length === 0 ? (
                <div style={{ padding: 12, color: "#6b7280" }}>
                  No featured guides yet.
                </div>
              ) : (
                featuredGuides.map((g) => (
                  <GuideCard key={g.id} guide={g} />
                ))
              )}
            </div>
          </div>

          <div className="card">
            <h3>Trending tags</h3>
            <div
              style={{
                marginTop: 8,
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              {tagsLoading ? (
                // show small skeleton placeholders while loading
                Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} height={28} />)
              ) : trendingTags.length === 0 ? (
                // fallback default tags if the collection is empty
                (function () {
                  const fallback = ["#roadtrip", "#mountains", "#beaches", "#food", "#photography", "#bike"];
                  return fallback.map((tag) => {
                    const id = tag.replace("#", "");
                    return (
                      <button
                        key={tag}
                        type="button"
                        className="tag"
                        aria-label={`Explore ${tag}`}
                        onClick={(e) => {
                          e.preventDefault();
                          // set the tag (without '#') and open inline Explore
                          setExploreInitialTag(id);
                          setExploreInlineOpen(true);
                        }}
                      >
                        {tag}
                      </button>
                    );
                  });
                })()
              ) : (
                (function () {
                  return trendingTags.map((t) => {
                    const id = String(t.id || t.name || "").replace(/^#/, "");
                    return (
                      <button
                        key={id || Math.random()}
                        type="button"
                        className="tag"
                        aria-label={`Explore ${id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          setExploreInitialTag(id);
                          setExploreInlineOpen(true);
                        }}
                      >
                        <span>#{id}</span>{" "}
                        <span style={{ fontSize: 12, color: "#6b7280" }}>{t.count ?? ""}</span>
                      </button>
                    );
                  });
                })()
              )}
            </div>
          </div>

          <Suspense fallback={<div className="card"><Skeleton height={180} /></div>}>
            <EditorPicksSection />
          </Suspense>


        </div>

        <aside className="right-column">
          <QuickStats />

          <div className="card small">
            <h4 style={{ marginTop: 0 }}>Suggested creators</h4>
            <div style={{ marginTop: 8 }}>
              {loading
                ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} height={52} />)
                : users.slice(0, 6).map((u) => <UserCard user={u} key={u.id} />)}
              {!loading && users.length === 0 && <div style={{ color: "#6b7280", paddingTop: 8 }}>No creators yet.</div>}
            </div>
          </div>
          <BookmarksPanel />
          <FollowedCreatorsPanel />
          <NewMembers users={users} loading={loading} limit={5} />

          <div className="card small">
            <h4 style={{ marginTop: 0 }}>Why Jift?</h4>
            <p style={{ color: "#6b7280", fontSize: 13 }}>
              A simple, social place for travelers to store memories, discover
              routes, and find like-minded creators.
            </p>
            <ul style={{ marginTop: 8, paddingLeft: 18, color: "#6b7280", fontSize: 13 }}>
              <li>Maps & live tracking</li>
              <li>Rich galleries & stories</li>
              <li>Creator-first features</li>
            </ul>
          </div>
        </aside>
      </section>

      {/* Inline Explore overlay */}
      {exploreInlineOpen && (
        <ExplorePage
          initialOpen={true}
          initialTag={exploreInitialTag}
          onClose={() => {
            setExploreInlineOpen(false);
            setExploreInitialTag(null);
          }}
        />
      )}

      {/* Auth modal */}
      {authOpen && <AuthModal onClose={() => setAuthOpen(false)} />}

      {/* AddTrip modal (if dynamically loaded) */}
      {addModalOpen && AddModalComp && (
        <Suspense fallback={null}>
          <AddModalComp
            currentUserId={currentUser?.uid || null}
            onClose={() => {
              setAddModalOpen(false);
            }}
            onSave={async (payload) => {
              if (!currentUser?.uid) {
                throw new Error("User not authenticated");
              }

              const tripId = await addTrip(payload, currentUser.uid);

              // return shape TripModal already understands
              return {
                id: tripId,
                slug: payload.slug,
              };
            }}
          />
        </Suspense>
      )}

    </div>
  );
}
